import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActionButtonVisibilityService } from '@netfoundry-ui/feature/action-button';
import { NetworkV2 } from '@netfoundry-ui/shared/model';
import { ApiService, URLS } from '@netfoundry-ui/shared/services';
import { forgetFixedColumnWidths, ROW_MENU_ICONS } from '@netfoundry-ui/ui/table';
import { escape } from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { DataTableFilterService, FilterObj, ListColumn, ListSortDir, ListTableService } from 'ziti-console-lib';
import { NetworksCombinedComponent } from './networks-combined.component';

const TABLE_ID = 'networks-list';
const NAME_FILTER = 'name';
const DEFAULT_PAGE_SIZE = 30;
const TEXT_FILTERS = [NAME_FILTER, 'status', 'region', 'productVersion', 'id'];
const STATUS_OPTIONS = ['NEW', 'PROVISIONING', 'PROVISIONED', 'UPDATING', 'SUSPENDED', 'ERROR', 'DELETING'].map(
    (status) => ({
        value: status,
        label: status,
    })
);

type NetworkRow = NetworkV2 & { actionList?: string[]; hideSelect?: boolean; selected?: boolean };

@Component({
    selector: 'app-networks-list',
    templateUrl: './networks-list.component.html',
    standalone: false,
})
export class NetworksListComponent extends NetworksCombinedComponent implements OnInit, OnDestroy {
    readonly tableId = TABLE_ID;
    readonly searchFilter = NAME_FILTER;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { pager: 'pages' as const };
    // The order of the row menu; each row shows the ones its action list holds.
    readonly menuItems = [
        { label: 'Firewall Info', action: 'firewall', icon: ROW_MENU_ICONS.view },
        { label: 'Notifications', action: 'notifications', icon: ROW_MENU_ICONS.events },
        { label: 'Download', action: 'download', icon: ROW_MENU_ICONS.download },
        { label: 'JSON', action: 'json', icon: ROW_MENU_ICONS.json },
        { label: 'Public Certificate', action: 'publicCert', icon: ROW_MENU_ICONS.view },
        { label: 'Rename', action: 'rename', icon: ROW_MENU_ICONS.edit },
        { label: 'Support Info', action: 'supportinfo', icon: ROW_MENU_ICONS.view },
        { label: 'Manage Config', action: 'configPort', icon: ROW_MENU_ICONS.edit },
        { label: 'Suspend', action: 'suspend', icon: ROW_MENU_ICONS.suspend },
        { label: 'Resume', action: 'resume', icon: ROW_MENU_ICONS.resume },
        { label: 'Resize', action: 'resize', icon: ROW_MENU_ICONS.resize },
        { label: 'Move Network', action: 'move', icon: ROW_MENU_ICONS.move },
        { label: 'Restart', action: 'restart', icon: ROW_MENU_ICONS.restart },
        { label: 'Restart All', action: 'restartAll', icon: ROW_MENU_ICONS.restart },
        { label: 'Create Backup', action: 'createBackup', icon: ROW_MENU_ICONS.backup },
        { label: 'Restore Backup', action: 'restoreBackup', icon: ROW_MENU_ICONS.restore },
        { label: 'Enable HA', action: 'enableHA', icon: ROW_MENU_ICONS.upgrade },
        { label: 'Enable FIPS', action: 'enableFips', icon: ROW_MENU_ICONS.upgrade },
        { label: 'Upgrade Network', action: 'upgrade', icon: ROW_MENU_ICONS.upgrade },
        { label: 'Migrate to V8', action: 'migrate', icon: ROW_MENU_ICONS.upgrade },
        { label: 'Delete', action: 'delete', icon: ROW_MENU_ICONS.delete },
    ];
    readonly headerActions = [{ label: 'Download Selected', action: 'downloadCsv', icon: ROW_MENU_ICONS.download }];
    readonly tabs = this.buildTabs();

    listColumns: ListColumn<NetworkRow>[] = [];

    private readonly tableFilters = inject(DataTableFilterService);
    private readonly createVisibility = inject(ActionButtonVisibilityService);
    private readonly api = inject(ApiService);
    private readonly listSub = new Subscription();
    private ready = false;

    get rows(): NetworkRow[] {
        (this.displayedItems as NetworkRow[]).forEach((row) => {
            row.hideSelect = !row.actionList?.includes('delete') || row.status !== 'PROVISIONED';
            // The old menu showed Suspend or Resume by the network's status, and the table has no Get or Download All.
            row.actionList = (row.actionList ?? []).filter(
                (action) =>
                    action !== 'get' &&
                    action !== 'downloadAll' &&
                    !(action === 'suspend' && row.status === 'SUSPENDED') &&
                    !(action === 'resume' && row.status !== 'SUSPENDED')
            );
        });
        return this.displayedItems as NetworkRow[];
    }

    get hasSelection(): boolean {
        return this.selectedNetworkIds.length > 0;
    }

    // Same rule the old plus/minus button hid itself by, so Create and Delete follow it.
    get createBlocked(): boolean {
        return !this.authorizationService.canCreateNetworks() || this.createVisibility.isCancelledAccount;
    }

    // The old button also hid the delete action once more than one network was selected.
    get deleteBlocked(): boolean {
        return this.createVisibility.isCancelledAccount || this.selectedNetworkIds.length > 1;
    }

    get hasPrev(): boolean {
        return this.page > 1;
    }

    get hasNext(): boolean {
        return this.page * this.pageSize < this.totalElements;
    }

    get startCount(): number {
        return this.displayedItems.length ? (this.page - 1) * this.pageSize + 1 : 0;
    }

    get endCount(): number {
        return this.displayedItems.length ? (this.page - 1) * this.pageSize + this.displayedItems.length : 0;
    }

    override async ngOnInit(): Promise<void> {
        // The base loads the provider column only once it has a grid to refresh, so give it a stand-in.
        this.gridObj = {};
        this.gridApi = { refreshCells: () => undefined };
        this.pageSize = ListTableService.readPersistedPageSize(TABLE_ID, DEFAULT_PAGE_SIZE);
        this.tableFilters.pageSize = this.pageSize;
        this.tableFilters.clearFilters();
        this.listColumns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.listColumns);
        this.listSub.add(
            this.tableFilters.filtersChanged.subscribe((filters) => {
                this.applyFilters(filters);
                if (this.ready) {
                    this.restartList();
                }
            })
        );
        this.listSub.add(
            this.tableFilters.pageChanged.pipe(skip(1)).subscribe((page) => {
                this.page = Math.max(1, Number(page) || 1);
                this.isLoading = true;
                this.refresh();
            })
        );
        this.listSub.add(
            this.tableFilters.pageSizeChanged.pipe(skip(1)).subscribe((size) => {
                this.pageSize = size;
                this.restartList();
            })
        );
        await super.ngOnInit();
        this.ready = true;
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listSub.unsubscribe();
        this.tableFilters.clearFilters();
    }

    onHeaderAction(action: string): void {
        if (action === 'add') {
            this.create();
        } else if (action === 'delete' && this.hasSelection) {
            this.openDeleteSelectedConfirm();
        }
    }

    onSelection(rows: NetworkRow[]): void {
        this.selectedNetworkIds = rows.map((row) => row.id);
        this.isDeleteAction = this.selectedNetworkIds.length > 0;
    }

    onAction(event: { action: string; item?: NetworkRow }): void {
        if (event.action === 'create') {
            this.create();
        } else if (event.action === 'downloadCsv' || event.item) {
            // Every row action goes to the same handler the old table used.
            this.actionRequested(event);
        }
    }

    private buildTabs(): { label: string; url: string; hidden: boolean }[] {
        const showProcesses = () =>
            this.authorizationService.canListNetworks() && this.featureService.disableNewNavigation;
        return [
            { label: 'Networks', url: URLS.OLD_NETWORK_LIST, hidden: false },
            {
                label: 'Network Processes',
                url: URLS.PROCESS_EXECUTIONS_LIST,
                get hidden() {
                    return !showProcesses();
                },
            },
        ];
    }

    private applyFilters(filters: FilterObj[]): void {
        const next: Record<string, string> = { networkGroupName: '' };
        TEXT_FILTERS.forEach((id) => (next[id] = ''));
        filters.forEach((item) => {
            if (item.columnId in next) {
                next[item.columnId] = String(item.value ?? '').trim();
            }
        });
        Object.assign(this.columnFilters, next);
        this.filterString = next[NAME_FILTER];
        this.filterApplied = Object.values(next).some((value) => !!value);
    }

    private restartList(): void {
        this.page = 1;
        this.tableFilters.currentPage = 1;
        this.isLoading = true;
        this.refresh();
    }

    private statusHtml(row: NetworkRow): string {
        const tone =
            row.status === 'PROVISIONED'
                ? 'online'
                : row.status === 'ERROR'
                ? 'failed'
                : row.status === 'SUSPENDED'
                ? 'offline'
                : 'progress';
        return `<span class="lt-status lt-status-${tone}">${escape(row.status || '')}</span>`;
    }

    private nameHtml(row: NetworkRow): string {
        const readOnly = this.api.isNetworkClusterReadOnly(row);
        const badges = `${row.haEnabled ? ' <span class="lt-chip">HA</span>' : ''}${
            row.fipsEnabled ? ' <span class="lt-chip">FIPS</span>' : ''
        }${readOnly ? ' <span class="lt-chip">Read Only</span>' : ''}`;
        return `<strong>${escape(row.name)}</strong>${badges}`;
    }

    private buildColumns(): ListColumn<NetworkRow>[] {
        const open = (ctx: { row: NetworkRow }) => this.goToDashboard(ctx.row);
        const info = (ctx: { row: NetworkRow }) => this.actionRequested({ action: 'firewall', item: ctx.row });
        const onSort = (key: string, dir: ListSortDir) => this.sort(key, dir === 'desc' ? 'DESC' : 'ASC');
        const columns: ListColumn<NetworkRow>[] = [
            {
                key: NAME_FILTER,
                label: 'Name',
                minWidth: 240,
                sortable: true,
                sortDir: 'asc',
                onSort,
                filterType: 'TEXTINPUT',
                cellHtml: (shim) => this.nameHtml(shim.data),
                onCellClick: open,
            },
            {
                key: 'networkGroupName',
                label: 'Network Group',
                minWidth: 150,
                valueFormatter: (shim) => (shim.data as any).networkGroupName || '',
                onCellClick: open,
            },
            {
                key: 'status',
                label: 'Status',
                width: 150,
                sortable: true,
                onSort,
                filterType: 'SELECT',
                filterPlaceholder: 'Any status',
                filterOptions: STATUS_OPTIONS,
                cellHtml: (shim) => this.statusHtml(shim.data),
                onCellClick: open,
            },
            {
                key: 'region',
                label: 'Region',
                width: 120,
                sortable: true,
                onSort,
                filterType: 'TEXTINPUT',
                valueFormatter: (shim) => shim.data.region || '',
                onCellClick: open,
            },
            {
                key: 'provider',
                label: 'Provider',
                width: 120,
                valueFormatter: (shim) => {
                    if (this.loadingProviders) {
                        return 'Loading Provider...';
                    }
                    return shim.data.networkController ? this.providers[shim.data.networkController.id] || '' : '';
                },
                onCellClick: open,
            },
            {
                key: 'size',
                label: 'Size',
                width: 100,
                sortable: true,
                onSort,
                valueFormatter: (shim) => (shim.data as any).size || '',
                onCellClick: open,
            },
            {
                key: 'productVersion',
                label: 'Version',
                width: 140,
                filterType: 'TEXTINPUT',
                cellHtml: (shim) => {
                    const upgradable =
                        this.authorizationService.canUpgradeNetwork(this.currentNetwork?.id) &&
                        (shim.data as any).isUpgradable;
                    return `${escape(shim.data.productVersion || '')}${
                        upgradable ? ' <span class="pi pi-arrow-circle-up"></span>' : ''
                    }`;
                },
                onCellClick: info,
            },
            {
                key: 'provisionedAt',
                label: 'Provision Date',
                minWidth: 150,
                valueFormatter: (shim) =>
                    shim.data.createdAt ? moment(shim.data.createdAt).local().format('M/D/YY h:mm a') : '',
                onCellClick: open,
            },
        ];
        if (this.featureService.powerUserEnabled) {
            columns.push(
                {
                    key: 'id',
                    label: 'ID',
                    minWidth: 260,
                    sortable: true,
                    onSort,
                    filterType: 'TEXTINPUT',
                    valueFormatter: (shim) => shim.data.id || '',
                },
                {
                    key: 'networkGroupId',
                    label: 'NW Group Id',
                    minWidth: 260,
                    valueFormatter: (shim) => shim.data.networkGroupId || '',
                }
            );
        }
        return columns;
    }
}
